import {Injectable, Injector, ViewContainerRef} from '@angular/core';
import {Decimal} from 'decimal.js';
import * as _ from 'lodash-es';
import {defer, EMPTY, NEVER, Observable, of} from 'rxjs';
import {map, switchMap, takeUntil} from 'rxjs/operators';
import {toApiDateTime} from '../../../common/dates';
import {AccountService} from '../../../components/accounts';
import {LastAccountService, LastAccountsToSetRec} from '../../../components/accounts/last-account.service';
import {destroy$} from '../../../components/destroyable';
import {InvPortfolioService} from '../../../components/investment-portfolio';
import {CONSTANT_CASH_LEVERAGE, LeverageService} from '../../../components/leverage';
import {
    getOrderQtyAssetType,
    getOrderQtyCorrectedSecurityInputParams,
    getOrderQtyMultiCurrencyInfo,
    getOrderQtyMultiCurrencyInfoForOrder,
    getQtyAndQtyAssetTypeFromOrder,
    OrderQtyMultiCurrencyService
} from '../../../components/order-qty-multi-currency';
import {Position} from '../../../components/position';
import {AlertFn, showAlertOnResponseErrorCodeMsg} from '../../../components/response-errors/response-error-code-msg';
import {
    getDefaultAsecurityLegsAccountDict,
    isAsecurityKey,
    OrderAspreaderAlgoParams,
    parseAsecurityConfig,
    prepareOrderAspreaderAlgoParamsAccounts
} from '../../../components/security/aspreader-security';
import {isSmartSecurityKey, SmartSecurityService} from '../../../components/smart-security';
import {SymbolDecimalsInputParams, SymbolDecimalsService} from '../../../components/symbol-decimals';
import {AccountType} from '../../../enums/account-type';
import {AllOrNone} from '../../../enums/all-or-none';
import {AssetType} from '../../../enums/asset-type';
import {isExchangeAllowedToTrade} from '../../../enums/exchange';
import {
    getOrderInputParamsByAlgoType,
    OrderAlgoType,
    parseAlgoTypeFromOrder,
    prepareAlgoTypeForOrder
} from '../../../enums/order-algo-type';
import {OrderTransactionType} from '../../../enums/order-transaction-type';
import {OrdType} from '../../../enums/order-type';
import {Side} from '../../../enums/side';
import {TimeInForce} from '../../../enums/time-in-force';
import {
    ExchangeAccountBase,
    ExchangeAccountBaseWithLeverage,
    ExchangeAccountId
} from '../../../models/exchange-account';
import {isOrderIcebergAllowed, NewOrder, Order, ReplaceOrCancelOrder} from '../../../models/order';
import {OrderSniperAlgoParams, OrderTwapAlgoParams} from '../../../models/order-algo-params';
import {Security} from '../../../models/security';
import {clearSecurityKey, parseSecurityKeyArr, SecurityKey} from '../../../models/security-key';
import {OrderService} from '../../../services/orders';
import {OrderFormValue} from '../float-widget-common/order-modal-form-block';
import {getAccountBaseByNewOrderParams, getNewOrderParamsByAccountBase} from './order-placing-utils';
import {
    AspreaderAlgoParamsError,
    getAspreaderAlgoParamsErrors
} from './widget-order-aspreader-algo-params-alert/aspreader-algo-params-errors';
import {
    getTwapAlgoParamsErrors,
    TwapAlgoParamsError
} from './widget-order-twap-algo-params-alert/twap-algo-params-errors';
import {PosClosingConfirmedParams} from './widget-pos-close-confirm-modal/pos-closing-confirmed-params';
import NumberFormat = Intl.NumberFormat;

export interface OrderCreationParams {
    security: Security;
    accountBase: ExchangeAccountBase;
    isConfirmationDisabled: boolean;
    isOneClickTradingEnabled: boolean;
    confirmModalAnchorVcRef: ViewContainerRef;
    disabledQtyStepValidation?: boolean; // [ECN-120] Force usage of qtyScale over qtyViewScale for qty validation
    disableMinQtyValidation?: boolean; // [ECN-120] Allow closing positions with qty < minQty
    saveLastAccount?: boolean;
    orderFormValue?: Partial<OrderFormValue>;
    isOrderDetails?: boolean; // Skips portfolio ID validation
}

export interface OrderCancelingParams extends Partial<OrderCreationParams> {
    security: Security;
    isConfirmationDisabled: boolean;
    confirmModalAnchorVcRef: ViewContainerRef;
}

export interface PositionClosingParams {
    position: Position;
    confirmModalAnchorVcRef: ViewContainerRef;
    investmentEntityId?: number | undefined;
    portfolioId?: number | undefined;
    leverage?: number | undefined;
}

export interface OrderConfirmationParams {
    security: Security;
    confirmModalAnchorVcRef: ViewContainerRef;
}

type AbstractOrderPlacementResult = unknown | never;

@Injectable()
export class OrderPlacingBaseService {

    public readonly destroy$ = destroy$();

    constructor(
        protected accountService: AccountService,
        protected orderService: OrderService,
        protected symbolDecimalsService: SymbolDecimalsService,
        protected injector: Injector,
        protected smartSecurityService: SmartSecurityService,
        protected invPortfolioService: InvPortfolioService,
        protected leverageService: LeverageService,
        protected orderQtyMultiCurrencyService: OrderQtyMultiCurrencyService,
        protected lastAccountService: LastAccountService
    ) {}

    private static creationToConfirmationParams(params: OrderCreationParams): OrderConfirmationParams {
        return {
            security:                params.security,
            confirmModalAnchorVcRef: params.confirmModalAnchorVcRef
        };
    }

    public initiateOrderPlacing(
        params: OrderCreationParams,
        newOrder: NewOrder
    ): Observable<AbstractOrderPlacementResult> {
        const result$ = isExchangeAllowedToTrade(params.security.securityExchange)
            ? this.canAccountTradeOnSecurity(params.security, newOrder.accountId).pipe(
                switchMap(canAccountTradeOnSecurity => canAccountTradeOnSecurity
                    ? params.isOneClickTradingEnabled
                        ? this.isPortfolioIdNotNeededOrSelected(params, newOrder.portfolioId)
                            ? this.initiateOrderCreation(params, newOrder)
                            : this.confirmSelectPortfolioId(
                                OrderPlacingBaseService.creationToConfirmationParams(params)
                            )
                        : this.initiateTradeUiCreation(params, newOrder)
                    : this.confirmAccountCreation(OrderPlacingBaseService.creationToConfirmationParams(params))
                )
            )
            : this.confirmExchangeSupport(OrderPlacingBaseService.creationToConfirmationParams(params));

        return result$.pipe(
            takeUntil(this.destroy$)
        );
    }

    public initiateOrderCanceling(
        params: OrderCancelingParams,
        orderToCancel: Order
    ): Observable<AbstractOrderPlacementResult> {
        const {
                  accountId,
                  orderQty,
                  price,
                  stopPx,
                  timeInForce,
                  side,
                  ordType,
                  clOrdId,
                  portfolioId,
                  investmentEntityId,
                  leverage,
                  algoType,
                  algoParams
              } = orderToCancel;

        const cancelOrderModel: ReplaceOrCancelOrder = {
            orderQty,
            price,
            stopPx,
            timeInForce,
            side,
            transactionType: OrderTransactionType.CANCEL,
            accountId,
            ordType,
            origClOrdId: clOrdId,
            portfolioId,
            investmentEntityId,
            leverage,
            algoType,
            algoParams
        };

        const result$ = this.initiateOrderPlacing(
            {
                ...params,
                accountBase:              getAccountBaseByNewOrderParams(orderToCancel),
                isOneClickTradingEnabled: true
            },
            cancelOrderModel
        );

        return result$.pipe(
            takeUntil(this.destroy$)
        );
    }

    public resolveOrderParams(params: OrderCreationParams, newOrder: NewOrder): Observable<NewOrder> {
        type Resolver = (order: NewOrder) => Observable<NewOrder>;

        const pipeResolves = (resolvers: Resolver[]): Resolver => {
            const callResolver = (order: NewOrder, index: number): Observable<NewOrder> =>
                index >= resolvers.length
                    ? of(order)
                    : (
                        resolvers[index](order).pipe(
                            switchMap((nextOrder: NewOrder) =>
                                callResolver(nextOrder, index + 1)
                            )
                        )
                    );

            return order => callResolver(order, 0);
        };

        const resolveClOrdId: Resolver = order => of<NewOrder>(
            order.clOrdId
                ? order
                : {
                    ...order,
                    clOrdId: crypto.randomUUID()
                }
        );

        const resolveAccount: Resolver = order => of<NewOrder>({
            ...order,
            ...getNewOrderParamsByAccountBase(params.accountBase)
        });

        const resolveSecurity: Resolver = order => of<NewOrder>({
            ...order,
            securityId: null,
            security:   clearSecurityKey(params.security) as Security
        });

        const resolveSmartAlgoType: Resolver = order => {
            let changes: Partial<NewOrder>;
            const algoType = parseAlgoTypeFromOrder(order.algoType);

            if (isSmartSecurityKey(params.security) && algoType === OrderAlgoType.GENERAL) {
                changes = {
                    algoType: OrderAlgoType.SMART
                };
            }

            return of(changes
                ? {...order, ...changes}
                : order
            );
        };

        const resolveAspreaderAlgoType: Resolver = order => {
            let changes: Partial<NewOrder>;

            if (isAsecurityKey(params.security)) {
                let algoParams = order.algoParams as OrderAspreaderAlgoParams;
                const asecurityConfig = parseAsecurityConfig(params.security);

                let legsAccounts = algoParams && algoParams.accountSecurities;

                if ((!legsAccounts || !legsAccounts.length) && params.isOneClickTradingEnabled) {
                    const legsAccountDict = getDefaultAsecurityLegsAccountDict(
                        params.security,
                        asecurityConfig.legs,
                        this.accountService
                    );

                    legsAccounts = prepareOrderAspreaderAlgoParamsAccounts(params.security, legsAccountDict);

                    algoParams = {
                        ...algoParams,
                        algoType: OrderAlgoType.AUTOSPREADER,
                        accountSecurities: legsAccounts
                    };
                }

                changes = {
                    ...order,
                    algoType:  OrderAlgoType.AUTOSPREADER,
                    accountId: legsAccounts[0].accountId,
                    algoParams
                };
            }

            return of(changes
                ? {...order, ...changes}
                : order
            );
        };

        const resolveLeverage: Resolver = order => {
            return this.leverageService.correctSideLeverage(
                params.security,
                params.accountBase,
                order.side,
                order.leverage
            ).pipe(
                map(leverage =>
                    leverage === order.leverage
                        ? order
                        : {...order, leverage}
                )
            );
        };

        const resolveQty: Resolver = order => {
            const {security} = params;
            const qtyMultiCurrencyInfo = getOrderQtyMultiCurrencyInfoForOrder(order, security);

            /**
             * Regardless of {@link OrderQtyMultiCurrencyInfo.isMultiCurrencyNativelySupported},
             * we need to read field {@link Order.orderQuoteQty} in order to convert it here if needed.
             */
            const BYPASS_NATIVE_MULT_CUR_SUPPORT = true;

            const {qty, qtyAssetType} = getQtyAndQtyAssetTypeFromOrder(
                security,
                newOrder,
                qtyMultiCurrencyInfo,
                BYPASS_NATIVE_MULT_CUR_SUPPORT,
                params.orderFormValue && params.orderFormValue.qtyAssetType
            );

            return this.orderQtyMultiCurrencyService.resolveOrderQty({
                security,
                qtyAssetType,
                qtyValue:    qty,
                limitPrice:  order.price,
                orderType:   order.ordType,
                side:        order.side,
                accountType: params.accountBase && params.accountBase.type,
                qtyMultiCurrencyInfo
            }).pipe(
                map(nativeQty => {
                    const resolveOrder: NewOrder = {
                        ...order,
                        orderQty:      nativeQty,
                        orderQuoteQty: (
                                           qtyMultiCurrencyInfo.isMultiCurrencyNativelySupported
                                           && qtyAssetType === AssetType.QUOTE
                                       )
                                           ? nativeQty
                                           : undefined
                    };

                    delete resolveOrder._orderBaseQty;

                    if (!qtyMultiCurrencyInfo.isMultiCurrencyNativelySupported) {
                        delete resolveOrder.orderQuoteQty;
                    }

                    return resolveOrder;
                })
            );
        };

        return pipeResolves([
            resolveClOrdId,
            resolveAccount,
            resolveSecurity,
            resolveQty,
            resolveSmartAlgoType,
            resolveAspreaderAlgoType,
            resolveLeverage
        ])(newOrder);
    }

    // tslint:disable:cyclomatic-complexity
    public getOrderForForm(
        security: Security,
        accountBase: ExchangeAccountBase,
        formValue: OrderFormValue,
        transactionType: OrderTransactionType,
        investmentEntityId: number | undefined,
        portfolioId: number | undefined,
        leverage: number
    ): NewOrder | ReplaceOrCancelOrder {
        const isReplaceOrCancel = transactionType === OrderTransactionType.REPLACE
            || transactionType === OrderTransactionType.CANCEL;

        const algoType = this.resolveAlgoTypeFromFormValue(formValue, security);
        const paramsByAlgoType = getOrderInputParamsByAlgoType(algoType);
        const isTwap = algoType === OrderAlgoType.TWAP;

        const ordType: OrdType = paramsByAlgoType.isOrderTypeReadonly
            ? paramsByAlgoType.orderType
            : formValue.orderType;

        const timeInForce: TimeInForce = paramsByAlgoType.isTimeInForceReadonly
            ? paramsByAlgoType.timeInForce
            : formValue.timeInForce;

        const allOrNone = formValue.allOrNone;

        const newOrder: NewOrder = {
            algoType:    prepareAlgoTypeForOrder(algoType),
            orderQty:    formValue.quantity,
            timeInForce,
            side:        formValue.side,
            securityId:  security.id,
            ordType,
            origClOrdId: isReplaceOrCancel ? formValue.origClOrdId : undefined,
            transactionType,
            investmentEntityId,
            portfolioId,

            ...getNewOrderParamsByAccountBase(accountBase),
            leverage: leverage || CONSTANT_CASH_LEVERAGE
        };

        const qtyMultiCurrencyInfo = getOrderQtyMultiCurrencyInfo(security, {
            orderType:   newOrder.ordType,
            side:        newOrder.side,
            accountType: accountBase && accountBase.type
        });

        if (ordType !== OrdType.MARKET) {
            newOrder.price = ordType === OrdType.STOP
                ? formValue.stopPrice
                : formValue.limitPrice;

            if (ordType === OrdType.STOP_LIMIT) {
                newOrder.stopPx = formValue.stopPrice;
            }
        }

        if (allOrNone === AllOrNone.YES) {
            newOrder.minQty = formValue.quantity;
        }

        const isIcebergEnabled = formValue.isIcebergEnabled
            && formValue.icebergQty
            && isOrderIcebergAllowed(security.securityExchange, newOrder.ordType);

        if (isIcebergEnabled) {
            newOrder.icebergQty = formValue.icebergQty;
            newOrder.minQty = formValue.quantity;
        }

        if (isTwap) {
            const [twapStartTime, twapEndTime] = formValue.twapTimeRange
                ? formValue.twapTimeRange.map(ts => ts ? toApiDateTime(ts) : undefined)
                : [undefined, undefined];

            (newOrder.algoParams as OrderTwapAlgoParams) = {
                algoType:       OrderAlgoType.TWAP,
                twapStartTime,
                twapEndTime,
                twapStepDelay:  formValue.twapStepDelay && formValue.twapStepDelayUnit
                                    ? formValue.twapStepDelay * formValue.twapStepDelayUnit
                                    : formValue.twapStepDelay,
                twapRandomQty:  formValue.twapRandomQty,
                twapRandomTime: formValue.twapRandomTime
            };
        } else if (algoType === OrderAlgoType.SNIPER) {
            (newOrder.algoParams as OrderSniperAlgoParams) = {
                algoType:       OrderAlgoType.SNIPER,
                sniperDelay:  formValue.sniperDelay && formValue.sniperDelayUnit
                                    ? formValue.sniperDelay * formValue.sniperDelayUnit
                                    : formValue.sniperDelay
            };
        } else if (algoType === OrderAlgoType.AUTOSPREADER) {
            const accountSecurities = prepareOrderAspreaderAlgoParamsAccounts(security, formValue.legsAccountDict);

            (newOrder.algoParams as OrderAspreaderAlgoParams) = {
                algoType,
                accountSecurities
            };

            newOrder.accountId = accountSecurities[0].accountId;
        }

        /**
         * [TRAD-101, TRAD-114] Pass qty in quote asset.
         */
        if (qtyMultiCurrencyInfo.isMultiCurrencyNativelySupported && formValue.qtyAssetType === AssetType.QUOTE) {
            newOrder.orderQuoteQty = newOrder.orderQty;
        }

        return newOrder;
    }

    public initiatePositionClosing(params: PositionClosingParams): Observable<AbstractOrderPlacementResult> {
        const {position, confirmModalAnchorVcRef} = params;
        const {security} = position;

        const result$ = isExchangeAllowedToTrade(security.securityExchange)
            ? this.canAccountTradeOnSecurity(security, position.accountId).pipe(
                switchMap(canAccountTradeOnSecurity => canAccountTradeOnSecurity
                    ? this.confirmAndSubmitPositionClosing(params)
                    : NEVER
                )
            )
            : this.confirmExchangeSupport({security, confirmModalAnchorVcRef});

        return result$.pipe(
            takeUntil(this.destroy$)
        );
    }

    protected initiateTradeUiCreation(params: OrderCreationParams, newOrder: NewOrder): Observable<never> {

        // Not implemented.
        console.warn('initiateTradeUiCreation (not implemented)', {params, newOrder});

        return EMPTY;
    }

    protected confirmOrderCreation(params: OrderConfirmationParams, newOrder: NewOrder): Observable<NewOrder> {

        // Not implemented.
        console.warn('confirmOrderCreation (not implemented)', {params, newOrder});

        return NEVER;
    }

    protected showMinNotionalAlert(params: OrderConfirmationParams, isIcebergAlert: boolean = false): Observable<never> {

        // Not implemented.
        console.warn('showMinNotionalAlert (not implemented)', {params, isIcebergAlert});

        return EMPTY;
    }

    protected showMinQuantityAlert(params: OrderConfirmationParams, newOrder: NewOrder): Observable<never> {

        // Not implemented.
        console.warn('showMinQuantityAlert (not implemented)', {params, newOrder});

        return EMPTY;
    }

    protected showMaxQuantityAlert(params: OrderConfirmationParams, newOrder: NewOrder): Observable<never> {

        // Not implemented.
        console.warn('showMaxQuantityAlert (not implemented)', {params, newOrder});

        return EMPTY;
    }

    protected showMinPriceAlert(params: OrderConfirmationParams): Observable<never> {

        // Not implemented.
        console.warn('showMinPriceAlert (not implemented)', {params});

        return EMPTY;
    }

    protected showMaxPriceAlert(params: OrderConfirmationParams): Observable<never> {

        // Not implemented.
        console.warn('showMaxPriceAlert (not implemented)', {params});

        return EMPTY;
    }

    protected showStepAlert({params, title, message, step, format}: {
        params: OrderConfirmationParams,
        title: string,
        message: string,
        step: number,
        format: NumberFormat
    }): Observable<never> {

        // Not implemented.
        console.warn('showStepAlert (not implemented)', {params, title, message, step, format});

        return EMPTY;
    }

    protected showCommonOrderValidationAlert(params: OrderConfirmationParams): Observable<never> {

        // Not implemented.
        console.warn('showCommonOrderValidationAlert (not implemented)', {params});

        return EMPTY;
    }

    protected showTwapAlgoParamsAlert(
        params: OrderConfirmationParams,
        twapAlgoParamsErrors: TwapAlgoParamsError[]
    ): Observable<never> {

        // Not implemented.
        console.warn('showTwapAlgoParamsAlert (not implemented)', {params, twapAlgoParamsErrors});

        return EMPTY;
    }

    protected showAspreaderAlgoParamsAlert(
        params: OrderConfirmationParams,
        aspreaderAlgoParamsErrors: AspreaderAlgoParamsError[]
    ): Observable<never> {

        // Not implemented.
        console.warn('showAspreaderAlgoParamsAlert (not implemented)', {params, aspreaderAlgoParamsErrors});

        return EMPTY;
    }

    protected confirmExchangeSupport(params: OrderConfirmationParams): Observable<never> {

        // Not implemented.
        console.warn('confirmExchangeSupport (not implemented)', {params});

        return EMPTY;
    }

    protected confirmAccountCreation(
        params: OrderConfirmationParams
    ): Observable<never> {

        // Not implemented.
        console.warn('confirmAccountCreation (not implemented)', {params});

        return EMPTY;
    }

    protected confirmSelectPortfolioId(params: OrderConfirmationParams): Observable<never> {

        // Not implemented.
        console.warn('confirmSelectPortfolioId (not implemented)', {params});

        return EMPTY;
    }

    protected confirmPositionClosing(params: PositionClosingParams): Observable<PosClosingConfirmedParams | never> {

        // Not implemented.
        console.warn('confirmPositionClosing (not implemented)', {params});

        return EMPTY;
    }

    protected getErrorAlertFn(): AlertFn {
        return undefined; // Use default alert modal.
    }

    private validateOrderPlacingValues(params: OrderCreationParams, newOrder: NewOrder): Observable<boolean | never> {
        return defer(() => {
            const {security} = params;
            const {min, max, steps} = this.getOrderValidationInputParams(params, newOrder);

            const isCancelOrder = newOrder.transactionType === OrderTransactionType.CANCEL;

            const isValueDefined = (value: number | unknown) =>
                _.isFinite(value);

            const areValuesDefined = (...values: Array<number | unknown>) =>
                values.every(isValueDefined);

            const isValueNotDefined = (value: undefined | null | unknown) =>
                _.isNil(value);

            const isValueValid = (value: number, valueMin: number, valueMax: number, valueStep: number): boolean =>
                value >= valueMin && value <= valueMax && new Decimal(value).mod(valueStep).isZero();

            const isMinQuantityValid = (): boolean =>
                isCancelOrder || (
                    newOrder.allOrNone === AllOrNone.YES
                        ? isValueValid(newOrder.minQty, min.quantity, max.quantity, steps.quantity)
                        : isValueNotDefined(newOrder.minQty)
                );

            const shouldPriceBeDefined = (): boolean =>
                newOrder.ordType === OrdType.LIMIT || newOrder.ordType === OrdType.STOP_LIMIT;

            const shouldStopPriceBeDefined = (): boolean =>
                newOrder.ordType === OrdType.STOP_LIMIT;

            const shouldMinQtyBeDefined = (): boolean =>
                newOrder.allOrNone === AllOrNone.YES;

            const isPriceValid = (): boolean =>
                isCancelOrder || (
                    shouldPriceBeDefined()
                        ? isValueValid(newOrder.price, min.price, max.price, steps.price)
                        : _.isNil(newOrder.price)
                );

            const isStopPriceValid = (): boolean =>
                isCancelOrder || (
                    shouldStopPriceBeDefined()
                        ? isValueValid(newOrder.stopPx, min.price, max.price, steps.price)
                        : _.isNil(newOrder.stopPx)
                );

            const isNotionalValid = (): boolean =>
                isCancelOrder || !shouldPriceBeDefined()
                || (
                    areValuesDefined(newOrder.price, newOrder.orderQty)
                    && new Decimal(newOrder.price).mul(newOrder.orderQty).gte(min.notional)
                );

            const isIcebergNotionalValid = (): boolean =>
                !shouldPriceBeDefined()
                || (
                    !areValuesDefined(newOrder.price, newOrder.icebergQty)
                    || new Decimal(newOrder.price).mul(newOrder.icebergQty).gte(min.notional)
                );

            const validatorWrapper = (
                condition: () => boolean,
                onError: () => Observable<never>
            ): () => Observable<boolean | never> =>
                () => condition() ? of(true) : onError();

            const minCondition = (value: number, valueMin: number): boolean =>
                value >= valueMin;

            const maxCondition = (value: number, valueMax: number): boolean =>
                value <= valueMax;

            const stepCondition = (value: number, valueStep: number): boolean =>
                areValuesDefined(value, valueStep)
                && new Decimal(value).mod(valueStep).isZero();

            const validators = [
                validatorWrapper(
                    () => isCancelOrder || params.disableMinQtyValidation
                        || minCondition(newOrder.orderQty, min.quantity),
                    () => this.showMinQuantityAlert(params, newOrder)
                ),
                validatorWrapper(
                    () => isCancelOrder || maxCondition(newOrder.orderQty, max.quantity),
                    () => this.showMaxQuantityAlert(params, newOrder)
                ),
                validatorWrapper(
                    () => isCancelOrder || !shouldPriceBeDefined() || minCondition(newOrder.price, min.price),
                    () => this.showMinPriceAlert(params)
                ),
                validatorWrapper(
                    () => isCancelOrder || !shouldPriceBeDefined() || maxCondition(newOrder.price, max.price),
                    () => this.showMaxPriceAlert(params)
                ),
                validatorWrapper(
                    () => isCancelOrder || isNotionalValid(),
                    () => this.showMinNotionalAlert(params)
                ),
                validatorWrapper(
                    () => isIcebergNotionalValid(),
                    () => this.showMinNotionalAlert(params, true)
                ),
                validatorWrapper(
                    () => isCancelOrder || (!shouldPriceBeDefined() || stepCondition(newOrder.price, steps.price))
                        && (!shouldStopPriceBeDefined() || stepCondition(newOrder.stopPx, steps.price)),
                    () => this.showPriceStepAlert(params, {shouldStopPriceBeDefined: shouldStopPriceBeDefined()})
                ),
                validatorWrapper(
                    () => isCancelOrder || stepCondition(newOrder.orderQty, steps.quantity)
                        && (!shouldMinQtyBeDefined() || stepCondition(newOrder.minQty, steps.quantity)),
                    () => this.showQtyStepAlert(params, {
                        shouldMinQtyBeDefined: shouldMinQtyBeDefined(),
                        qtyStep:               steps.quantity
                    })
                ),
                validatorWrapper(
                    () => !getTwapAlgoParamsErrors(newOrder).length,
                    () => this.showTwapAlgoParamsAlert(params, getTwapAlgoParamsErrors(newOrder))
                ),
                validatorWrapper(
                    () => !getAspreaderAlgoParamsErrors(newOrder, security, this.accountService).length,
                    () => this.showAspreaderAlgoParamsAlert(
                        params,
                        getAspreaderAlgoParamsErrors(newOrder, security, this.accountService)
                    )
                ),
                validatorWrapper(
                    () =>
                        stepCondition(newOrder.orderQty, steps.quantity)
                        && isMinQuantityValid()
                        && isPriceValid()
                        && isStopPriceValid(),
                    () => {
                        console.warn(
                            'Invalid order params',
                            {newOrder, params},
                            {
                                isStepValid:        stepCondition(newOrder.orderQty, steps.quantity),
                                isMinQuantityValid: isMinQuantityValid(),
                                isPriceValid:       isPriceValid(),
                                isStopPriceValid:   isStopPriceValid()
                            }
                        );

                        return this.showCommonOrderValidationAlert(params);
                    }
                )
            ];

            return validators.reduce(
                (validation$, validator) => validation$.pipe(
                    switchMap(() => validator())
                ),
                of(true)
            );
        });
    }

    private initiateOrderCreation(params: OrderCreationParams, newOrder: NewOrder): Observable<any> {
        const doCreateOrder = (finalNewOrder: NewOrder) => {
            if (params.saveLastAccount) {
                this.setLastAccountFromNewOrder(finalNewOrder);
            }

            return this.orderService.createOrder(finalNewOrder).pipe(
                showAlertOnResponseErrorCodeMsg({
                    injector: this.injector,
                    vcRef:    params.confirmModalAnchorVcRef,
                    alertFn:  this.getErrorAlertFn()
                })
            );
        };

        const doCreateOrderWithConfirmation = (finalNewOrder: NewOrder) =>
            this.confirmOrderCreation(OrderPlacingBaseService.creationToConfirmationParams(params), finalNewOrder).pipe(
                switchMap(() => doCreateOrder(finalNewOrder))
            );

        return this.resolveOrderParams(params, newOrder).pipe(
            switchMap(finalNewOrder =>
                this.validateOrderPlacingValues(params, finalNewOrder).pipe(
                    switchMap(() => !params.isConfirmationDisabled
                        ? doCreateOrderWithConfirmation(finalNewOrder)
                        : doCreateOrder(finalNewOrder)
                    )
                )
            )
        );
    }

    private showPriceStepAlert(
        params: OrderConfirmationParams,
        {shouldStopPriceBeDefined}: {
            shouldStopPriceBeDefined: boolean;
        }
    ): Observable<never> {
        const TITLE = 'components.widgets.trade-widget.price_step_error_title';
        const message = shouldStopPriceBeDefined
            ? 'components.widgets.trade-widget.price_and_stop_price_step_error_message'
            : 'components.widgets.trade-widget.price_step_error_message';

        const {steps} = this.symbolDecimalsService.getSecurityInputParams(params.security);
        const securityFormats = this.symbolDecimalsService.getSecurityFormats(params.security);

        return this.showStepAlert({
            params,
            title:  TITLE,
            message,
            step:   steps.price,
            format: securityFormats.priceFormat
        });
    }

    private showQtyStepAlert(
        params: OrderConfirmationParams,
        {shouldMinQtyBeDefined, qtyStep}: {
            shouldMinQtyBeDefined: boolean;
            qtyStep: number;
        }
    ): Observable<never> {
        const TITLE = 'components.widgets.trade-widget.qty_step_error_title';
        const message = shouldMinQtyBeDefined
            ? 'components.widgets.trade-widget.qty_and_min_qty_step_error_message'
            : 'components.widgets.trade-widget.qty_step_error_message';

        const securityFormats = this.symbolDecimalsService.getSecurityFormats(params.security);

        return this.showStepAlert({
            params,
            title:  TITLE,
            message,
            step:   qtyStep,
            format: securityFormats.quantityFormat
        });
    }

    private canAccountTradeOnSecurity(securityKey: SecurityKey, accountId: ExchangeAccountId): Observable<boolean> {
        return isAsecurityKey(securityKey)
            ? of(true)
            : this.smartSecurityService.getSecurityUserAccounts(securityKey).pipe(
                map(accounts =>
                    isSmartSecurityKey(securityKey)
                        ? !!accounts.length
                        : !!accounts.find(account => account.id === accountId)
                )
            );
    }

    private isPortfolioIdNotNeededOrSelected(params: OrderCreationParams, portfolioId: number): boolean {
        const isOrderDetails: boolean = params.isOrderDetails;
        const portfolios = this.invPortfolioService.getCached().portfolios;

        return portfolios && portfolios.length && !isOrderDetails
            ? !!portfolios.find(portfolio => portfolio.id === portfolioId)
            : true;
    }

    private confirmAndSubmitPositionClosing(params: PositionClosingParams): Observable<AbstractOrderPlacementResult> {
        return this.confirmPositionClosing(params).pipe(
            switchMap(confirmedParams => this.submitPositionClosing({
                ...params,
                ...confirmedParams
            }))
        );
    }

    private submitPositionClosing(params: PositionClosingParams): Observable<AbstractOrderPlacementResult> {
        const {position, confirmModalAnchorVcRef, leverage, investmentEntityId, portfolioId} = params;
        const {security, accountId} = position;

        const tradeSide: Side = (position.quantity < 0)
            ? Side.BUY
            : Side.SELL;

        return this.initiateOrderCreation(
            {
                confirmModalAnchorVcRef,
                security,
                accountBase:               {
                    id:   accountId,
                    type: AccountType.MARGIN
                },
                isConfirmationDisabled:    true,
                isOneClickTradingEnabled:  true,
                disabledQtyStepValidation: true,
                disableMinQtyValidation:   true
            },
            {
                orderQty:        Math.abs(position.quantity),
                timeInForce:     TimeInForce.GTC,
                side:            tradeSide,
                securityId:      security.id,
                transactionType: OrderTransactionType.NEW,
                accountId,
                ordType:         OrdType.MARKET,
                investmentEntityId,
                portfolioId,
                leverage,

                /**
                 * [HED-759] The text tells API to not open new position.
                 * This flag is required for some exchanges, e.g. OKEx.
                 */
                text: `CLOSE_POSITION#${position.id}`
            }
        );
    }

    /**
     * Returns parameters for price/quantity validation in new order before placing.
     */
    private getOrderValidationInputParams(
        params: OrderCreationParams,
        newOrder: NewOrder
    ): SymbolDecimalsInputParams {
        const {security, disabledQtyStepValidation} = params;
        const qtyMultiCurrencyInfo = getOrderQtyMultiCurrencyInfoForOrder(newOrder, security);

        return getOrderQtyCorrectedSecurityInputParams({
            qtyMultiCurrencyInfo,
            securityInputParams: this.symbolDecimalsService.getSecurityInputParams(security),

            /**
             * We are validating qty in the asset, that will be sent to API.
             */
            toNativeQtyAssetType: true,

            qtyAssetType:    getOrderQtyAssetType(security, newOrder, qtyMultiCurrencyInfo),
            isMinStepNeeded: disabledQtyStepValidation
        });
    }

    private setLastAccountFromNewOrder(newOrder: NewOrder): void {
        const orderSecurityKey = newOrder.security;
        const algoType = parseAlgoTypeFromOrder(newOrder.algoType);
        const getAccountTypeByLeverage = (leverage: number): AccountType =>
            leverage ? AccountType.MARGIN : AccountType.CASH;

        if (algoType === OrderAlgoType.AUTOSPREADER) {
            const algoParams = (newOrder.algoParams || {}) as OrderAspreaderAlgoParams;

            const lastAccountRecs = algoParams.accountSecurities.map<LastAccountsToSetRec>(algoParamsItem => ({
                accountBase:  {
                    id:       algoParamsItem.accountId,
                    type:     getAccountTypeByLeverage(algoParamsItem.leverage),
                    leverage: algoParamsItem.leverage
                },
                securityKeys: [orderSecurityKey, parseSecurityKeyArr(algoParamsItem.securityKey)]
            }));

            this.lastAccountService.setLastAccounts(lastAccountRecs);
        } else {
            const accountBase: ExchangeAccountBaseWithLeverage = {
                id:       newOrder.accountId,
                type:     getAccountTypeByLeverage(newOrder.leverage),
                leverage: newOrder.leverage || undefined
            };

            this.lastAccountService.setLastAccount([orderSecurityKey], accountBase);
        }
    }

    private resolveAlgoTypeFromFormValue(formValue: OrderFormValue, security: Security): OrderAlgoType {
        const defaultAlgoType = parseAlgoTypeFromOrder(formValue.algoType);

        const resolvers: Array<() => [boolean, OrderAlgoType]> = [
            () => [!!formValue.algoType, defaultAlgoType],
            () => [isAsecurityKey(security), OrderAlgoType.AUTOSPREADER],
            () => [isSmartSecurityKey(security), OrderAlgoType.SMART]
        ];

        let result: OrderAlgoType = defaultAlgoType;

        for (const resolver of resolvers) {
            const [condition, algoType] = resolver();

            if (condition) {
                result = algoType;
                break;
            }
        }

        return result;
    }

}
